import * as Sentry from '@sentry/react';
/* @ts-expect-error: the type from shared component is wrong */
import { setErrorReporter } from '@veritone/react-components';

// Errors from the transcript editor (react-components), e.g. a failed save. For each one
// the browser builds one record and:
// - prints it to the console
// - sends it to Illuminate's backend, which logs it as one JSON line (searchable in the
//   cluster's Kibana; this works where Sentry isn't allowed, e.g. Gov)
// - sends it to Sentry too, where a DSN is configured
// The user sees the error id and can quote it to support.

// no look-alikes (0/O, 1/I/L), so the id can be read out over the phone
// cspell:disable-next-line
const ERROR_ID_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const MAX_REPORTS_PER_MINUTE = 10;

export const createErrorId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return (
    'ILLUMINATE_' +
    Array.from(bytes, (b) => ERROR_ID_CHARS[b % ERROR_ID_CHARS.length]).join('')
  );
};

export const CLIENT_ERROR_TYPE = 'illuminate-client-error';

export interface ClientErrorRecord {
  type: typeof CLIENT_ERROR_TYPE;
  level: 'error';
  message: string;
  timestamp: string;
  errorId: string;
  errorName?: string;
  errorMessage: string;
  // what the editor was doing, e.g. 'Failed to save transcript edits.'
  context?: string;
  tdoId?: string;
  saveType?: string;
  url?: string;
  release?: string;
  apiRoot?: string;
  // the asset the failed save's edits are based on
  assetId?: string;
  // set by react-components' saveAsset on a failed request
  httpStatus?: number;
  errorCode?: string;
  durationMs?: number;
  // the top of the stack: which call failed
  stack?: string;
  userAgent?: string;
  online?: boolean;
  orgId?: string;
  userId?: string;
}

// the backend runs next to the app (nginx proxies /api); locally it's on port 3002
const getBackendHost = () => {
  const host = window.location.protocol + '//' + window.location.host;
  return host.includes('local.veritone')
    ? host.replace(/:[0-9]{4}$/, ':3002')
    : host;
};

let recentReports: number[] = [];
// a burst of errors (e.g. a failing retry loop) logs only the first few
const withinRateLimit = (now = Date.now()) => {
  recentReports = recentReports.filter((at) => now - at < 60_000);
  if (recentReports.length >= MAX_REPORTS_PER_MINUTE) {
    return false;
  }
  recentReports.push(now);
  return true;
};

export const resetRateLimit = () => {
  recentReports = [];
};

// react-components' saveAsset adds these to the errors it throws
const requestDetails = (error: Error) => {
  const { status, code, durationMs } = error as Error & {
    status?: unknown;
    code?: unknown;
    durationMs?: unknown;
  };
  return {
    httpStatus: typeof status === 'number' ? status : undefined,
    errorCode: typeof code === 'string' ? code.slice(0, 100) : undefined,
    durationMs: typeof durationMs === 'number' ? durationMs : undefined,
  };
};

// enough frames to see which call failed, e.g. saveAsset or cleanupUserEditedAssets
const topOfStack = (error: Error) =>
  error.stack?.split('\n').slice(0, 6).join('\n').slice(0, 2000);

// e.g. a 401 after the session expired: the error is then only in the browser console
const warnNotLogged = (errorId: string, reason: string) => {
  console.warn(`Error report ${errorId} was not logged: ${reason}`);
};

export const sendClientErrorReport = async (
  record: ClientErrorRecord,
  token: string
) => {
  try {
    const response = await fetch(`${getBackendHost()}/api/v1/client-errors`, {
      method: 'POST',
      // still sent if the tab is closing
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-veritone-application': window.config?.veritoneAppId,
      },
      body: JSON.stringify(record),
    });
    if (!response.ok) {
      warnNotLogged(record.errorId, `HTTP ${response.status}`);
    }
  } catch (e) {
    warnNotLogged(record.errorId, (e as Error).message);
  }
};

export const registerClientErrorReporter = ({
  getToken,
  getUser,
  sentryEnabled,
}: {
  getToken: () => string | null;
  getUser: () => { userId?: string; orgId?: string };
  sentryEnabled: boolean;
}) => {
  setErrorReporter(
    (
      error: Error,
      {
        message,
        tags,
      }: {
        message: string;
        tags: { tdoId?: string; saveType?: string; assetId?: string };
      }
    ) => {
      const errorId = createErrorId();
      const record: ClientErrorRecord = {
        type: CLIENT_ERROR_TYPE,
        level: 'error',
        message: 'Illuminate client error',
        timestamp: new Date().toISOString(),
        errorId,
        errorName: error.name,
        errorMessage: (error.message || '').slice(0, 1000),
        context: message.slice(0, 1000),
        tdoId: tags.tdoId,
        saveType: tags.saveType,
        url: window.location.pathname.slice(0, 500),
        // set by the Sentry build plugin: the git commit
        release: (globalThis as { SENTRY_RELEASE?: { id?: string } })
          .SENTRY_RELEASE?.id,
        apiRoot: window.config?.apiRoot,
        assetId: tags.assetId,
        ...requestDetails(error),
        stack: topOfStack(error),
        userAgent: navigator.userAgent.slice(0, 300),
        online: navigator.onLine,
        ...getUser(),
      };
      console.error(`Illuminate client error ${errorId}`, record);
      if (sentryEnabled) {
        Sentry.captureException(error, {
          tags: {
            feature: 'transcript-editor',
            errorId,
            tdoId: record.tdoId,
            saveType: record.saveType,
            assetId: record.assetId,
          },
          contexts: { illuminateClientError: { ...record } },
        });
      }
      const token = getToken();
      if (!token) {
        warnNotLogged(errorId, 'no session');
      } else if (!withinRateLimit()) {
        warnNotLogged(errorId, 'too many reports');
      } else {
        sendClientErrorReport(record, token);
      }
      return errorId;
    }
  );
};
